import React, { useEffect, useState } from "react";
import Card from "src/components/Cards/Basic";
import { Button, Form } from "react-bootstrap";
import axios from "src/config/axios";
import { useParams } from "react-router-dom";
import useBusiness from "src/pages/AdminDashboard/Businesses/util/useBusiness";
import {
  showErrorToast,
  showSuccessToast,
  showValidationErrorToast,
} from "src/components/Toasts";
import InputText from "src/components/Forms/InputText";
import SelectBox from "src/components/Forms/SelectBox";

const DEFAULT_ACCOUNT = {
  environment: "demo",
  merchant_id: "",
  pos_isv_commission_percent: 0,
};

const DEFAULT_TERMINAL = {
  terminal_id: "",
  virtual_terminal_id: "",
};

const ENVIRONMENT_OPTIONS = [
  { value: "demo", label: "Demo" },
  { value: "live", label: "Live" },
];

const VivaAccountsSettingsPage = () => {
  const [vivaAccounts, setVivaAccounts] = useState([]);
  const [accountFormValues, setAccountFormValues] = useState({});
  const [terminalFormValues, setTerminalFormValues] = useState({});
  const [newAccount, setNewAccount] = useState(DEFAULT_ACCOUNT);
  const { id } = useParams();
  const business = useBusiness(id);

  useEffect(() => {
    loadVivaAccounts();
  }, [id]);

  const loadVivaAccounts = () => {
    axios
      .get(`/api/v1/admin/businesses/${id}/viva_accounts.json`)
      .then(({ data }) => {
        const entries = data.entries ?? [];
        const nextAccountFormValues = {};
        const nextTerminalFormValues = {};

        entries.forEach((account) => {
          nextAccountFormValues[account.id] = {
            environment: account.environment,
            merchant_id: account.merchant_id ?? "",
            pos_isv_commission_percent: account.pos_isv_commission_percent,
          };
          nextTerminalFormValues[account.id] = DEFAULT_TERMINAL;
        });

        setVivaAccounts(entries);
        setAccountFormValues(nextAccountFormValues);
        setTerminalFormValues(nextTerminalFormValues);
      })
      .catch(() => showErrorToast());
  };

  const updateAccountFormValue = (accountId, key, value) => {
    setAccountFormValues((previous) => ({
      ...previous,
      [accountId]: {
        ...previous[accountId],
        [key]: value,
      },
    }));
  };

  const updateTerminalFormValue = (accountId, key, value) => {
    setTerminalFormValues((previous) => ({
      ...previous,
      [accountId]: {
        ...(previous[accountId] ?? DEFAULT_TERMINAL),
        [key]: value,
      },
    }));
  };

  const createAccount = () => {
    axios
      .post(`/api/v1/admin/businesses/${id}/viva_accounts.json`, {
        viva_account: newAccount,
      })
      .then(() => {
        showSuccessToast();
        setNewAccount(DEFAULT_ACCOUNT);
        loadVivaAccounts();
      })
      .catch((response) => showValidationErrorToast(response));
  };

  const updateAccount = (accountId) => {
    const account = accountFormValues[accountId];
    if (!account) return;

    axios
      .put(`/api/v1/admin/businesses/${id}/viva_accounts/${accountId}.json`, {
        viva_account: account,
      })
      .then(() => {
        showSuccessToast();
        loadVivaAccounts();
      })
      .catch((response) => showValidationErrorToast(response));
  };

  const deleteAccount = (accountId) => {
    axios
      .delete(`/api/v1/admin/businesses/${id}/viva_accounts/${accountId}.json`)
      .then(() => {
        showSuccessToast();
        loadVivaAccounts();
      })
      .catch((response) => showValidationErrorToast(response));
  };

  const createTerminal = (accountId) => {
    const terminal = terminalFormValues[accountId] ?? DEFAULT_TERMINAL;

    axios
      .post(
        `/api/v1/admin/businesses/${id}/viva_accounts/${accountId}/viva_terminals.json`,
        { viva_terminal: terminal }
      )
      .then(() => {
        showSuccessToast();
        setTerminalFormValues((previous) => ({
          ...previous,
          [accountId]: DEFAULT_TERMINAL,
        }));
        loadVivaAccounts();
      })
      .catch((response) => showValidationErrorToast(response));
  };

  const deleteTerminal = (accountId, terminalId) => {
    axios
      .delete(
        `/api/v1/admin/businesses/${id}/viva_accounts/${accountId}/viva_terminals/${terminalId}.json`
      )
      .then(() => {
        showSuccessToast();
        loadVivaAccounts();
      })
      .catch((response) => showValidationErrorToast(response));
  };

  return (
    <Card title={`Viva Accounts von ${business?.name ?? ""}`}>
      <div className="table-responsive">
        <table className="table table-bordered" width="100%" cellSpacing="0">
          <thead>
            <tr>
              <th>Umgebung</th>
              <th>Merchant ID</th>
              <th>Connected Account ID</th>
              <th>ISV Kommission</th>
              <th>Terminals</th>
              <th>Aktionen</th>
            </tr>
          </thead>
          <tbody>
            {vivaAccounts.map((account) => (
              <tr key={account.id}>
                <td style={{ minWidth: "150px" }}>
                  <SelectBox
                    value={accountFormValues[account.id]?.environment ?? "demo"}
                    onChange={(e) =>
                      updateAccountFormValue(
                        account.id,
                        "environment",
                        e.target.value
                      )
                    }
                    options={ENVIRONMENT_OPTIONS}
                    option_name="label"
                    option_value="value"
                  />
                </td>
                <td style={{ minWidth: "280px" }}>
                  <InputText
                    value={accountFormValues[account.id]?.merchant_id ?? ""}
                    onChange={(e) =>
                      updateAccountFormValue(
                        account.id,
                        "merchant_id",
                        e.target.value
                      )
                    }
                  />
                </td>
                <td>{account.connected_account_id}</td>
                <td>
                  <InputText
                    type="number"
                    step="0.01"
                    value={
                      accountFormValues[account.id]
                        ?.pos_isv_commission_percent ?? ""
                    }
                    onChange={(e) =>
                      updateAccountFormValue(
                        account.id,
                        "pos_isv_commission_percent",
                        e.target.value
                      )
                    }
                  />
                </td>
                <td style={{ minWidth: "420px" }}>
                  {(account.viva_terminals ?? []).length > 0 && (
                    <table className="table table-sm table-bordered mb-3">
                      <thead>
                        <tr>
                          <th>Terminal ID</th>
                          <th>Virtual Terminal ID</th>
                          <th>Aktionen</th>
                        </tr>
                      </thead>
                      <tbody>
                        {account.viva_terminals.map((terminal) => (
                          <tr key={terminal.id}>
                            <td>{terminal.terminal_id}</td>
                            <td>{terminal.virtual_terminal_id}</td>
                            <td>
                              <Button
                                variant="outline-danger"
                                size="sm"
                                onClick={() =>
                                  deleteTerminal(account.id, terminal.id)
                                }
                              >
                                Löschen
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                  <div className="d-flex align-items-end">
                    <div className="mr-2 flex-fill">
                      <Form.Group className="mb-0">
                        <Form.Label>Terminal ID</Form.Label>
                        <InputText
                          value={
                            terminalFormValues[account.id]?.terminal_id ?? ""
                          }
                          onChange={(e) =>
                            updateTerminalFormValue(
                              account.id,
                              "terminal_id",
                              e.target.value
                            )
                          }
                        />
                      </Form.Group>
                    </div>
                    <div className="mr-2 flex-fill">
                      <Form.Group className="mb-0">
                        <Form.Label>Virtual Terminal ID</Form.Label>
                        <InputText
                          value={
                            terminalFormValues[account.id]
                              ?.virtual_terminal_id ?? ""
                          }
                          onChange={(e) =>
                            updateTerminalFormValue(
                              account.id,
                              "virtual_terminal_id",
                              e.target.value
                            )
                          }
                        />
                      </Form.Group>
                    </div>
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={() => createTerminal(account.id)}
                    >
                      Terminal erstellen
                    </Button>
                  </div>
                </td>
                <td style={{ whiteSpace: "nowrap" }}>
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => updateAccount(account.id)}
                  >
                    Speichern
                  </Button>
                  <Button
                    className="ml-2"
                    variant="outline-danger"
                    size="sm"
                    onClick={() => deleteAccount(account.id)}
                  >
                    Löschen
                  </Button>
                </td>
              </tr>
            ))}
            <tr>
              <td colSpan={6} className="bg-light">
                <strong>Neuer Viva Account (noch nicht gespeichert)</strong>
              </td>
            </tr>
            <tr>
              <td>
                <SelectBox
                  value={newAccount.environment}
                  onChange={(e) => {
                    const value = e.target.value;
                    setNewAccount((previous) => ({
                      ...previous,
                      environment: value,
                    }));
                  }}
                  options={ENVIRONMENT_OPTIONS}
                  option_name="label"
                  option_value="value"
                />
              </td>
              <td />
              <td>
                <InputText
                  value={newAccount.merchant_id}
                  onChange={(e) => {
                    const value = e.target.value;
                    setNewAccount((previous) => ({
                      ...previous,
                      merchant_id: value,
                    }));
                  }}
                />
              </td>
              <td>
                <InputText
                  type="number"
                  step="0.01"
                  value={newAccount.pos_isv_commission_percent}
                  onChange={(e) => {
                    const value = e.target.value;
                    setNewAccount((previous) => ({
                      ...previous,
                      pos_isv_commission_percent: value,
                    }));
                  }}
                />
              </td>
              <td />
              <td>
                <Button variant="primary" size="sm" onClick={createAccount}>
                  Neu erstellen
                </Button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      {vivaAccounts.length === 0 && (
        <small>Keine Viva Accounts vorhanden.</small>
      )}
    </Card>
  );
};

export default VivaAccountsSettingsPage;
